import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import CircularProgress from "@/components/common/CircularProgress";
import { KnowledgeArticleView } from "@/components/psychologist/home/KnowledgeArticleView";
import { getArticleById, useKnowledgeBase } from "@/hooks/useKnowledgeBase";
import { useNzJourneyStatus } from "@/hooks/useNzJourneyStatus";
import { useIsPwaApp } from "@/pwa/PwaModeContext";

/**
 * Si `url` apunta a un artículo de la Home (`/home?article=<id>`, relativo o absoluto),
 * devuelve el id del artículo; si no, null — en ese caso el link se abre como siempre.
 */
export function getHomeArticleId(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const parsed = new URL(url, window.location.origin);
    if (!/\/home\/?$/.test(parsed.pathname)) return null;
    return parsed.searchParams.get("article");
  } catch {
    return null;
  }
}

interface HomeArticleDialogProps {
  articleId: string | null;
  onClose: () => void;
}

/**
 * Artículo de la Home abierto en una ventana encima de Comunidad — así una novedad puede
 * linkear a "la política completa" sin sacar a nadie de la pantalla, y funciona también
 * para quienes no tienen acceso a la Home (NZ y O recién egresadas de NZ). A quienes sí
 * tienen Home se les muestra además "Ver en la Home →", para que sepan dónde vive.
 */
export const HomeArticleDialog = ({ articleId, onClose }: HomeArticleDialogProps) => {
  const navigate = useNavigate();
  const { categories, isLoading, error } = useKnowledgeBase();
  const { canAccessHome } = useNzJourneyStatus();
  // La app mobile no tiene Home: no se ofrece ir ahí (el artículo se lee igual, en esta ventana).
  const isPwaApp = useIsPwaApp();
  const article = articleId ? getArticleById(categories, articleId) : undefined;

  return (
    <Dialog open={!!articleId} onOpenChange={(open) => { if (!open) onClose(); }}>
      {/* Mismo fondo que la Home (AppShell, contentBackground de "home"): KnowledgeArticleView
          está pensado para fondo oscuro (texto blanco). Va también con `sm:` porque
          DialogContent trae `sm:bg-white` de base. */}
      <DialogContent className="max-h-[85vh] overflow-y-auto border-none bg-[#423568] p-6 sm:max-w-3xl sm:bg-[#423568]">
        {canAccessHome && !isPwaApp && articleId && article && (
          <button
            type="button"
            onClick={() => navigate(`/home?article=${encodeURIComponent(articleId)}`)}
            className="mb-2 ml-auto flex items-center gap-1 text-xs font-semibold text-psi-lime hover:underline"
          >
            Ver en la Home <ArrowRight size={13} />
          </button>
        )}
        {isLoading ? (
          <div className="flex justify-center py-10"><CircularProgress /></div>
        ) : article ? (
          <KnowledgeArticleView article={article} onBack={onClose} />
        ) : (
          <p className="m-0 py-6 text-center text-sm text-white/70">
            {error ? "No pudimos cargar el artículo. Probá de nuevo en un rato." : "Este artículo ya no está disponible."}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HomeArticleDialog;
